import styled from '@emotion/styled';

const tableInnerBorderColor = 'rgba(0, 0, 0, .10)';

// need to use fake shadow because we are applying the shadow to each td
// if we use normal shadow, there will be overlapped shadows
const edgeShadow = (position: 'top' | 'bottom' | 'left' | 'right'): string => {
  let offsetX = 0;
  let offsetY = 0;

  if (position === 'top') {
    offsetX = 0;
    offsetY = 1;
  }

  if (position === 'bottom') {
    offsetX = 0;
    offsetY = -1;
  }

  if (position === 'left') {
    offsetX = 1;
    offsetY = 0;
  }

  if (position === 'right') {
    offsetX = -1;
    offsetY = 0;
  }

  return [
    `${offsetX * 1}px ${offsetY * 1}px 0 rgba(0, 0, 0, .01)`,
    `${offsetX * 2}px ${offsetY * 2}px 0 rgba(0, 0, 0, .01)`,
    `${offsetX * 3}px ${offsetY * 3}px 0 rgba(0, 0, 0, .009)`,
    `${offsetX * 4}px ${offsetY * 4}px 0 rgba(0, 0, 0, .009)`,
    `${offsetX * 5}px ${offsetY * 5}px 0 rgba(0, 0, 0, .009)`,
    `${offsetX * 6}px ${offsetY * 6}px 0 rgba(0, 0, 0, .007)`,
    `${offsetX * 7}px ${offsetY * 7}px 0 rgba(0, 0, 0, .005)`,
    `${offsetX * 8}px ${offsetY * 8}px 0 rgba(0, 0, 0, .003)`,
  ].join(',');
};

export type DataForStyle = {
  fixTopHeaders: boolean;
  fixLeftHeaders: boolean;
  leftOverflowed: boolean;
  rightOverflowed: boolean;
  topOverflowed: boolean;
  bottomOverflowed: boolean;
  verticallyScrolled: boolean;
  noTopHeaders: boolean;
  noLeftHeaders: boolean;
};

export const InfoTableComponent = styled('div')({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
});

export const TableWrap = styled('div')({
  borderWidth: 1,
  borderStyle: 'solid',
  borderColor: tableInnerBorderColor,
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  maxWidth: '100%',
  overflow: 'hidden',
});

export const ScrollAreaContent = styled('div')<{
  dataForStyle: DataForStyle;
}>(
  ({
    dataForStyle: {
      fixTopHeaders,
      fixLeftHeaders,
      leftOverflowed,
      topOverflowed,
      verticallyScrolled,
      noLeftHeaders,
    },
  }) => ({
    overflowY: 'auto',
    overflowX: 'auto',
    width: '100%',

    '& > table': {
      borderCollapse: 'separate',
      borderSpacing: 0,
      minWidth: '100%',

      // every cell
      'th, td': {
        textAlign: 'left',
        padding: '1em',
        borderBottom: `1px solid ${tableInnerBorderColor}`,
      },

      // top and left header cells
      th: {
        fontWeight: 600,
      },

      'tbody tr:last-child': {
        'th, td': {
          borderBottom: 'none',
        },
      },

      // top header cells
      'thead th': {
        position: fixTopHeaders ? 'sticky' : 'static',
        top: !fixTopHeaders && fixLeftHeaders ? 'auto' : 0,
        zIndex: 1,

        ...(fixTopHeaders && topOverflowed
          ? { zIndex: 3, background: 'white', boxShadow: edgeShadow('top') }
          : {}),
      },

      // TODO: set max with for fixed left headers for small screen
      // left header cells
      'tbody th': {
        position: fixLeftHeaders ? 'sticky' : 'static',
        left: 0,
        zIndex: 1,
        borderRight: `1px solid ${tableInnerBorderColor}`,

        ...(fixLeftHeaders && leftOverflowed
          ? { zIndex: 3, background: 'white', boxShadow: edgeShadow('left') }
          : {}),
      },

      // the left-most top header cell
      'thead th:first-of-type': {
        position:
          fixTopHeaders || (!noLeftHeaders && fixLeftHeaders)
            ? 'sticky'
            : 'static',
        left: fixTopHeaders && !fixLeftHeaders ? 'auto' : 0,
        zIndex: noLeftHeaders && !topOverflowed ? 1 : 2,
        borderRight: noLeftHeaders
          ? 'none'
          : `1px solid ${tableInnerBorderColor}`,

        ...(fixLeftHeaders && leftOverflowed
          ? { zIndex: 4, background: 'white' }
          : {}),

        // hide the shadow for header of the first item in the first column (top left)
        // and use the shadow from 'tbody tr:first-of-type th:after' instead
        // this is to avoid shadows overlapping
        ...(fixLeftHeaders && leftOverflowed && verticallyScrolled
          ? {
              boxShadow: 'none',
            }
          : {}),
      },

      // shadow for the first item in the column
      'tbody tr:first-of-type th':
        fixLeftHeaders && leftOverflowed
          ? {
              '&:after': {
                content: "''",
                position: 'absolute',
                height: '5em',
                top: '-5em',
                right: -1,
                width: '90%',
                background: 'white',
                boxShadow: edgeShadow('left'),
              },
            }
          : {},
    },
  })
);

const ScrollButtonStyle = {
  position: 'absolute' as const,
  zIndex: 3,
  top: 0,
  bottom: 0,
  width: 40,
  background: 'rgba(255, 255, 255, 1)',
  color: tableInnerBorderColor,
  border: 'none',
  cursor: 'pointer',
  fontSize: 14,

  svg: {
    width: 14,
  },
};

export const ScrollAreaScrollLeft = styled('button')({
  ...ScrollButtonStyle,
  left: -40,
});

export const ScrollAreaScrollRight = styled('button')({
  ...ScrollButtonStyle,
  right: -40,
});

// the top shadow that hint there is content hidden on the top
export const TopOverflowShadow = styled('div')({
  content: "''",
  position: 'absolute',
  zIndex: 1,
  top: -40,
  height: 40,
  left: 0,
  right: 0,
  background: 'rgba(255, 255, 255, 1)',
  boxShadow: edgeShadow('top'),
});

// the bottom shadow that hint there is content hidden on the bottom
export const BottomOverflowShadow = styled('div')({
  content: "''",
  position: 'absolute',
  zIndex: 2,
  left: 0,
  right: 0,
  bottom: -40,
  height: 40,
  background: 'rgba(255, 255, 255, 1)',
  color: tableInnerBorderColor,
  border: 'none',
  cursor: 'pointer',
  fontSize: 14,
  boxShadow: edgeShadow('bottom'),
});

// the left shadow that hint there is content hidden on the left
export const LeftOverflowShadow = styled('div')({
  content: "''",
  position: 'absolute',
  zIndex: 1,
  left: -40,
  width: 40,
  top: 0,
  bottom: 0,
  background: 'rgba(255, 255, 255, 1)',
  boxShadow: edgeShadow('left'),
});

// the right shadow that hint there is content hidden on the right
export const RightOverflowShadow = styled('div')({
  content: "''",
  position: 'absolute',
  zIndex: 2,
  top: 0,
  bottom: 0,
  width: 40,
  right: -40,
  background: 'white',
  boxShadow: edgeShadow('right'),
});
